import { createElement, memo, useCallback, useMemo, useRef, useState, useEffect } from '@max/max';
import View from '@hfe/max-view';
import Text from '@hfe/max-text';
import ScrollView from '@hfe/max-scrollview';
import SlideModal from '@max/leez-slide-modal';
import SearchArea from './SearchArea';
import CityList from './CityList';
import LetterIndex from './LetterIndex';
import { groupCitiesByLetter, getLetters, convertCardGroupsToCities } from './cityUtil';
import ModalBaseContainer from '@max/leez-modal-base-container';
import TopView from '@max/leez-top-view';
import './index.scss';
import Image from '@hfe/max-image';
import ModalCityItem from './ModalCityItem';
import Button from '@hfe/max-button';
import Sensor from '@max/mbc-sensor';
import VapView from '../vap-view';
import { CITY_SELECTOR_CITY_VAP } from '../../const';
import Animated from '@max/mbc-animated';
import AnimateView from '@max/mbc-animate-view';
import { getFullScreenSize } from '../../utils/layout';
const SHOW_CITY_COUNT = 5;
const { fullScreenWidth, fullScreenHeight } = getFullScreenSize();
function CityFilterModal(props) {
    const { visible, cardGroupInfos, onClose, onSelectCity, changeLimit, onOpened, onCityCardShow, onClickChangeButton, onClickSearchButton } = props;
    const [showFiveCityModal, setShowFiveCityModal] = useState(visible);
    const [showMoreCityModal, setShowMoreCityModal] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [groupPositions, setGroupPositions] = useState([]);
    const [fiveCities, setFiveCities] = useState([]);
    const haveChangedTimeRef = useRef(0);
    const [showChangeButton, setShowChangeButton] = useState(true);
    const scrollViewRef = useRef(null);
    const isFirstOpenRef = useRef(true);
    // 动画状态
    const contentOpacity = useRef(new Animated.Value(0));
    const contentScale = useRef(new Animated.Value(0.5));
    const changeBtnOpacity = useRef(new Animated.Value(0));
    const cities = useMemo(() => convertCardGroupsToCities(cardGroupInfos), [cardGroupInfos]);
    const groups = useMemo(() => groupCitiesByLetter(cities), [cities]);
    const letters = useMemo(() => getLetters(groups), [groups]);
    const handleLetterSelect = useCallback((letter) => {
        const index = letters.indexOf(letter);
        if (index < 0 || groupPositions[index] === undefined)
            return;
        scrollViewRef.current?.scrollTo({
            y: groupPositions[index],
            animated: true,
            duration: 100
        });
    }, [letters, groupPositions]);
    function handleSelectCity(city) {
        setSearchKeyword('');
        onSelectCity(city);
        handleCloseCityModal();
    }
    useEffect(() => {
        setShowFiveCityModal(visible);
    }, [visible]);
    useEffect(() => {
        setFiveCities(cities.slice(0, 5));
    }, [cities]);
    /**
     * 五张免单卡出现动画
     * 时间线：1.64s ~ 1.84s 透明度 0%→100%，中心缩放 50%→110%
     * 1.96s 缩放 110%→100%
     */
    const playContentAnimation = useCallback(() => {
        contentOpacity.current.setValue(0);
        contentScale.current.setValue(0.5);
        setTimeout(() => {
            Animated.parallel([Animated.timing(contentOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true
                }), Animated.sequence([Animated.timing(contentScale.current, {
                        toValue: 1.1,
                        duration: 200,
                        useNativeDriver: true
                    }), Animated.timing(contentScale.current, {
                        toValue: 1,
                        duration: 120,
                        useNativeDriver: true
                    })])]).start();
        }, 1640);
    }, []);
    /**
     * 换一批按钮出现动画
     * 时间线：0s ~ 0.2s 透明度 0%→100%
     */
    const playChangeBtnAnimation = useCallback(() => {
        changeBtnOpacity.current.setValue(0);
        setTimeout(() => {
            Animated.parallel([Animated.timing(changeBtnOpacity.current, {
                    toValue: 1,
                    duration: 200,
                    useNativeDriver: true
                })]).start();
        }, 1640);
    }, []);
    function handleChangeCity() {
        const nextFiveCityStart = (haveChangedTimeRef.current + 1) * SHOW_CITY_COUNT;
        const nextFiveCity = cities.slice(nextFiveCityStart, nextFiveCityStart + SHOW_CITY_COUNT);
        if (haveChangedTimeRef.current < changeLimit && nextFiveCity.length === 5) {
            setFiveCities(nextFiveCity);
            haveChangedTimeRef.current++;
            setShowChangeButton(haveChangedTimeRef.current < changeLimit);
        }
        if (haveChangedTimeRef.current > changeLimit || (haveChangedTimeRef.current + 1) * SHOW_CITY_COUNT + SHOW_CITY_COUNT > cities.length) {
            setShowChangeButton(false);
        }
    }
    function handleSearch() {
        setShowFiveCityModal(false);
        setShowMoreCityModal(true);
    }
    function handleCloseCityModal() {
        setShowFiveCityModal(false);
        setShowMoreCityModal(false);
        onClose();
    }
    const isFirstOpen = isFirstOpenRef.current;
    return <>
      <TopView>
        <ModalBaseContainer visible={showFiveCityModal} useRNModal={false} type="fade" showMask onOpened={() => {
        onOpened();
        if (isFirstOpen) {
            isFirstOpenRef.current = false;
        }
    }} onMaskPress={() => {
        handleCloseCityModal();
    }}>
          <View className="cfe-five-city-container" style={{
        height: fullScreenHeight
    }} onClick={handleCloseCityModal}>
            {isFirstOpen && <View className="cfe-five-city-vap-petal">
                <VapView {...CITY_SELECTOR_CITY_VAP} autoPlay loop={false} keepLastFrame isFullScreen height={fullScreenHeight} width={fullScreenWidth} widthHeightRatio={750 / 1624} onStart={() => {
        playContentAnimation();
        playChangeBtnAnimation();
    }}/>
              </View>}
            <AnimateView className="cfe-five-city-content-wrapper" animateStyles={{
        opacity: isFirstOpen ? contentOpacity.current : undefined,
        transform: isFirstOpen ? [{
                scale: contentScale.current
            }] : undefined
    }}>
              <Image className="cfe-five-city-header-img" resizeMode="contain" source={{
        uri: 'https://p0.meituan.net/ingee/9a053184ca15e853f2afba1f80036d6a31566.png'
    }}/>
              <View className="cfe-five-city-fir-line">
                {fiveCities?.slice(0, 3)?.map((city, cityIndex) => <Sensor className="cfe-five-city-item" once onAppear={() => {
        onCityCardShow(city);
    }}>
                    <ModalCityItem onSelectCity={handleSelectCity} cardGroupInfoVO={city} showHandVap={cityIndex === 0} index={cityIndex}/>
                  </Sensor>)}
              </View>
              <View className="cfe-five-city-sec-line">
                {fiveCities?.slice(3, 5)?.map((city, cityIndex) => <Sensor className="cfe-five-city-item" once onAppear={() => {
        onCityCardShow(city);
    }}>
                    <ModalCityItem onSelectCity={handleSelectCity} cardGroupInfoVO={city} showHandVap={false} index={cityIndex + 3}/>
                  </Sensor>)}
              </View>
            </AnimateView>
            <AnimateView animateStyles={{
        opacity: isFirstOpen ? changeBtnOpacity.current : undefined
    }} style={{
        width: '750rpx'
    }}>
              <View x-if={showChangeButton} className="cfe-five-city-two-button-wrapper">
                <Button className="cfe-five-city-search-button" onPress={() => {
        onClickSearchButton();
        handleSearch();
    }}>
                  <Text className="cfe-five-city-search-button-text">搜索</Text>
                </Button>
                <Button className="cfe-five-city-change-button" onPress={() => {
        onClickChangeButton();
        handleChangeCity();
    }}>
                  <Text className="cfe-five-city-change-button-text">换一批</Text>
                </Button>
              </View>
              <View x-if={!showChangeButton} className="cfe-five-city-one-button-wrapper">
                <Button className="cfe-five-city-search-button" onPress={() => {
        onClickSearchButton();
        handleSearch();
    }}>
                  <Text className="cfe-five-city-search-button-text">搜索</Text>
                </Button>
              </View>

              <View className="cfe-five-city-close-button-wrapper">
                <Button className="cfe-five-city-close-button" onPress={() => {
        handleCloseCityModal();
    }}>
                  <Image className="cfe-five-city-close-button-img" source={{
        uri: 'https://p0.meituan.net/ingee/dacb6cc62b5d35e0e749fc7a73bbe7821798.png'
    }}/>
                </Button>
              </View>
            </AnimateView>
          </View>
        </ModalBaseContainer>
      </TopView>
      <SlideModal visible={showMoreCityModal} showHeader={false} showCloseBtn={false} maskClosable addBottomSpace={false} style={{
        borderTopLeftRadius: '36rpx',
        borderTopRightRadius: '36rpx',
        height: '1200rpx'
    }} onMaskPress={() => {
        handleCloseCityModal();
    }}>
        <View className="cfe-modal-container">
          <SearchArea cities={cities} onClose={() => {
        handleCloseCityModal();
    }} onSearchChange={setSearchKeyword} onSelectCity={handleSelectCity} searchKeyword={searchKeyword}/>

          {searchKeyword === '' && <View className="cfe-scroll-wrap">
              <ScrollView ref={scrollViewRef} className="cfe-scroll-view" showsVerticalScrollIndicator={false}>
                <CityList groups={groups} onSelectCity={handleSelectCity} onEmitPositions={setGroupPositions}/>
              </ScrollView>
              <LetterIndex letters={letters} groupPositions={groupPositions} onLetterSelect={handleLetterSelect}/>
            </View>}
        </View>
      </SlideModal>
    </>;
}
export default memo(CityFilterModal);
